import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { ProofImage } from "../../components/ProofImage";
import { Trash2 } from "lucide-react";
import { localeOf } from "../../i18n";

export default function ReviewQueue() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [reasons, setReasons] = useState({});
  const [busy, setBusy] = useState(null);
  const locale = localeOf(i18n.language);

  const load = () => api.get("/submissions/review", { params: { status: tab } })
    .then(({ data }) => setItems(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (sub, decision) => {
    setBusy(sub.id);
    try {
      await api.patch(`/submissions/${sub.id}/review`, { decision, reason: reasons[sub.id] || "" });
      toast.success(t(`status.${decision}`));
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const removePhoto = async (sub) => {
    try {
      await api.delete(`/submissions/${sub.id}/photo`);
      toast.success(t("review.removePhoto"));
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="review-queue-page">
      <h1 className="font-display text-3xl font-extrabold">{t("review.queue")}</h1>
      <div className="flex gap-2">
        {["pending", "approved", "rejected", "all"].map((k) => (
          <button key={k} data-testid={`review-tab-${k}`} onClick={() => setTab(k)}
            className={`h-11 rounded-full px-4 text-sm font-bold ${tab === k ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
            {k === "all" ? t("common.actions") : t(`status.${k}`)}
          </button>
        ))}
      </div>

      {items.length === 0 && (
        <p data-testid="review-empty" className="vy-card p-8 text-center text-sm font-semibold text-muted-foreground">
          {t("review.empty")}
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {items.map((s) => (
          <div key={s.id} className="vy-card p-5" data-testid={`review-card-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-lg font-bold">{s.mission_title}</p>
                <p className="text-xs text-muted-foreground">
                  {s.user_name} · {s.event_title} · {fmtDate(s.created_at, locale)}
                </p>
              </div>
              <StatusBadge status={s.status} />
            </div>

            {s.note && <p className="mt-3 rounded-xl bg-muted p-3 text-sm">{s.note}</p>}
            {s.file_path && <ProofImage path={s.file_path} className="mt-3" testId={`review-photo-${s.id}`} />}
            {s.image_deleted && (
              <p className="mt-3 text-xs font-semibold text-muted-foreground">{t("missions.proofRemoved")}</p>
            )}
            {s.review_reason && (
              <p className="mt-2 text-xs font-medium text-destructive">{s.review_reason}</p>
            )}

            {(s.status === "pending" || (s.status !== "pending" && user?.role === "admin")) && (
              <Textarea data-testid={`review-reason-input-${s.id}`} rows={2} className="mt-3"
                placeholder={t("review.rejectReason")}
                value={reasons[s.id] || ""}
                onChange={(e) => setReasons({ ...reasons, [s.id]: e.target.value })} />
            )}
            {s.status === "pending" && (
              <div className="mt-3 flex gap-2">
                <Button data-testid={`review-approve-btn-${s.id}`} disabled={busy === s.id}
                  onClick={() => decide(s, "approved")}
                  className="h-12 flex-1 rounded-full bg-[hsl(var(--success))] font-bold text-white hover:opacity-90">
                  {t("review.approve")} (+{s.mission_points})
                </Button>
                <Button data-testid={`review-reject-btn-${s.id}`} variant="outline"
                  disabled={busy === s.id || !(reasons[s.id] || "").trim()}
                  onClick={() => decide(s, "rejected")}
                  className="h-12 flex-1 rounded-full font-bold text-destructive">
                  {t("review.reject")}
                </Button>
              </div>
            )}
            {s.status !== "pending" && user?.role === "admin" && (
              <div className="mt-3 flex gap-2">
                <Button data-testid={`review-override-approve-${s.id}`} variant="outline"
                  onClick={() => decide(s, "approved")} className="h-11 rounded-full font-semibold">
                  {t("review.approve")}
                </Button>
                <Button data-testid={`review-override-reject-${s.id}`} variant="outline"
                  disabled={!(reasons[s.id] || "").trim()}
                  onClick={() => decide(s, "rejected")} className="h-11 rounded-full font-semibold">
                  {t("review.reject")}
                </Button>
              </div>
            )}
            {user?.role === "admin" && s.file_path && (
              <Button data-testid={`review-remove-photo-${s.id}`} variant="ghost"
                onClick={() => removePhoto(s)} className="mt-2 h-10 rounded-full text-destructive">
                <Trash2 className="mr-1 h-4 w-4" />{t("review.removePhoto")}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
